import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { ChevronDown, Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/utils';

const SelectContext = React.createContext<{
  value: string | string[];
  onValueChange: (value: string | string[]) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  multiple: boolean;
}>({
  value: '',
  onValueChange: () => {},
  open: false,
  setOpen: () => {},
  multiple: false,
});

interface SelectProps {
  value?: string | string[];
  defaultValue?: string | string[];
  onValueChange?: (value: string | string[]) => void;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  multiple?: boolean;
  children: React.ReactNode;
}

const Select = ({
  value,
  defaultValue = '',
  onValueChange,
  open,
  defaultOpen = false,
  onOpenChange,
  multiple = false,
  children,
}: SelectProps) => {
  const [internalValue, setInternalValue] = React.useState<string | string[]>(defaultValue);
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  
  const currentValue = value !== undefined ? value : internalValue;
  const currentOpen = open !== undefined ? open : internalOpen;
  
  const handleValueChange = React.useCallback((newValue: string | string[]) => {
    if (onValueChange) {
      onValueChange(newValue);
    } else {
      setInternalValue(newValue);
    }
  }, [onValueChange]);
  
  const handleOpenChange = React.useCallback((newOpen: boolean) => {
    if (onOpenChange) {
      onOpenChange(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
  }, [onOpenChange]);

  return (
    <SelectContext.Provider value={{ value: currentValue, onValueChange: handleValueChange, open: currentOpen, setOpen: handleOpenChange, multiple }}>
      <div className="relative w-full">
        {children}
      </div>
    </SelectContext.Provider>
  );
};
Select.displayName = 'Select';

interface SelectTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  children: React.ReactNode;
}

const SelectTrigger = React.forwardRef<HTMLButtonElement, SelectTriggerProps>(
  ({ className, children, ...props }, ref) => {
    const context = React.useContext(SelectContext);
    
    return (
      <button
        ref={ref}
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        onClick={() => context.setOpen(!context.open)}
        aria-expanded={context.open}
        {...props}
      >
        {children}
        <ChevronsUpDown className="h-4 w-4 opacity-50" />
      </button>
    );
  }
);
SelectTrigger.displayName = 'SelectTrigger';

interface SelectValueProps {
  placeholder?: string;
  className?: string;
}

const SelectValue = ({ placeholder, className }: SelectValueProps) => {
  const context = React.useContext(SelectContext);
  
  if (!context.value || (Array.isArray(context.value) && context.value.length === 0)) {
    return <span className={cn('text-muted-foreground', className)}>{placeholder}</span>;
  }
  
  if (Array.isArray(context.value)) {
    return <span className={className}>{context.value.length} selected</span>;
  }
  
  return <span className={className}>{context.value}</span>;
};
SelectValue.displayName = 'SelectValue';

interface SelectContentProps {
  className?: string;
  children: React.ReactNode;
}

const SelectContent = React.forwardRef<HTMLDivElement, SelectContentProps>(
  ({ className, children, ...props }, ref) => {
    const context = React.useContext(SelectContext);
    
    if (!context.open) return null;
    
    return ReactDOM.createPortal(
      <div
        ref={ref}
        className={cn(
          'absolute z-50 top-full left-0 mt-1 min-w-[8rem] w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          className
        )}
        {...props}
      >
        {children}
      </div>,
      document.body
    );
  }
);
SelectContent.displayName = 'SelectContent';

interface SelectItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  value: string;
  className?: string;
  children: React.ReactNode;
}

const SelectItem = React.forwardRef<HTMLButtonElement, SelectItemProps>(
  ({ value, className, children, ...props }, ref) => {
    const context = React.useContext(SelectContext);
    
    const isSelected = Array.isArray(context.value)
      ? context.value.includes(value)
      : context.value === value;
    
    const handleSelect = () => {
      if (context.multiple && Array.isArray(context.value)) {
        const newValue = isSelected
          ? context.value.filter(v => v !== value)
          : [...context.value, value];
        context.onValueChange(newValue);
      } else {
        context.onValueChange(value);
        context.setOpen(false);
      }
    };
    
    return (
      <button
        ref={ref}
        className={cn(
          'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground hover:bg-accent hover:text-accent-foreground',
          className
        )}
        onClick={handleSelect}
        {...props}
      >
        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
          {isSelected && <Check className="h-4 w-4" />}
        </span>
        {children}
      </button>
    );
  }
);
SelectItem.displayName = 'SelectItem';

interface SelectSeparatorProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

const SelectSeparator = React.forwardRef<HTMLDivElement, SelectSeparatorProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('-mx-1 my-1 h-px bg-border', className)}
      {...props}
    />
  )
);
SelectSeparator.displayName = 'SelectSeparator';

export { Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectSeparator };